import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { map, get, reduce, isEmpty, size, has, chain, find } from "lodash";
import { push } from "store/actions/router";
import { FiChevronLeft } from "react-icons/fi";
import { getPaginatedContributions } from "actions/payrollActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { payrollConstants, employerConstants } from "actions/types";
import { toast } from "react-toastify";
import { formatCurrency, formatAmount } from "utils/number";
import { activeBankSelector } from "store/selectors/bank";
import { approvePayroll } from "actions/payrollActions";
import { processedStatuses } from "statics/payroll";
import {
  usersWithNoContributionChangeSelector,
  usersWithPendingContributionsSelector,
} from "store/selectors/user";
import {
  approveContributionChanges,
  deleteEmployerLinks,
} from "actions/employerActions";

import moment from "moment";
import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import Kpi from "components/Kpi";
import StatusBox from "components/StatusBox";
import Paginator from "components/Paginator";
import IconTable from "components/IconTable";
import PayrollStatementDownloader from "../PayrollStatementDownloader";
import AuthorizationText from "components/AuthorizationText";
import type { RootState } from "types/state";
import type { ScheduledPayrollInput } from "types/graphqlSchema";

// Shape of a contribution/change entry as read in this component. The
// underlying redux slice is `unknown`-typed (payroll slice not yet modeled),
// so this is a local view of the fields actually accessed here.
interface ContributionEntry {
  employeeId: string;
  amount?: number;
  userCurrentContributionAmount?: number;
  userNewContributionAmount?: number;
  id?: string;
  isUserWhoLeft?: boolean;
  isLockedUser?: boolean;
  userProfile?: {
    firstName?: string;
    lastName?: string;
    lastFourOfSsn?: string;
  };
  // present on skippedContributions entries
  contribution?: { amount?: number };
}

// Dictionaries keyed by employeeId; truthiness of an entry signals membership.
type ContributionDict = Record<string, ContributionEntry>;

// The pay period found in state.employer.currentPayrolls for this payPeriodId.
interface PayPeriod {
  status?: string;
  taxYear?: number | string;
  onOrAfterPayrollProcessingDate?: boolean;
  payrollProcessingDate?: string;
  payPeriodEnd?: string;
  payPeriodStart?: string;
}

const updatingActions = [
  employerConstants.APPROVE_CONTRIBUTION_CHANGES,
  employerConstants.DELETE_EMPLOYER_LINKS,
  payrollConstants.APPROVE_PAYROLL,
];

const isLoadingSelector = createLoadingSelector(
  payrollConstants.GET_PAGINATED_CONTRIBUTIONS
);
const isErrorSelector = createErrorSelector([
  payrollConstants.GET_PAGINATED_CONTRIBUTIONS,
  ...updatingActions,
]);
const updatingSelector = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const allPending = usersWithPendingContributionsSelector(state);
  const contributionsWithNoChange =
    usersWithNoContributionChangeSelector(state);
  const allContributions = {
    ...allPending,
    ...contributionsWithNoChange,
  };

  const payroll = state.employer.currentPayrolls.find(
    (payroll) => payroll.payPeriodId === ownProps.payPeriodId
  ) as PayPeriod | undefined;

  return {
    payroll,
    isFetching: isLoadingSelector(state),
    pendingChanges: state.payroll.paginatedPendingContributionChanges,
    // PRESERVE: `count` is not a key on the payroll slice (it resolves to
    // undefined at runtime); pendingChanges/pendingChangesCount are also unread
    // dead props. Kept verbatim — cast so the absent field typechecks.
    pendingChangesCount: (state.payroll as { count?: number }).count,
    error: isErrorSelector(state),
    // Always defined: the lodash default of "" guarantees a string even when
    // no active bank exists. (PropTypes marked this .isRequired, but it is in
    // fact a mapState-provided string, not a required parent prop.)
    bankAccountNumber: get(
      activeBankSelector(state),
      "accountId",
      ""
    ) as string,
    contributionChanges: state.payroll.paginatedContributions as
      | ContributionEntry[]
      | undefined,
    contributionChangesCount: state.payroll.paginatedContributionsCount,
    linkedEmployees: state.employer.linkedEmployees as ContributionEntry[],
    contributionsWithNoChange:
      contributionsWithNoChange as unknown as ContributionDict,
    isUpdating: updatingSelector(state),
    allContributions: allContributions as unknown as ContributionDict,
  };
};

const mapDispatchToProps = {
  getPaginatedContributions,
  approveContributionChanges,
  deleteEmployerLinks,
  approvePayroll,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// Props supplied by the parent <Summary .../> render in PayrollWorkflow.
// stepName / goToNamedStep / isActive etc. are injected at runtime by
// react-step-wizard (the parent omits them) — typed optional, non-null
// asserted at unconditional call sites.
interface OwnProps {
  goToNamedStep?: (stepName: string) => void;
  isActive?: boolean;
  stepName?: string;
  group: { id: string; name: string };
  payPeriodId?: string;
  usersToExclude?: string[];
  usersWhoLeftPlan?: ContributionEntry[];
  lockedUserContributions?: ContributionEntry[];
  keptPriorContributions: ContributionDict;
  approvedContributions: ContributionDict;
  skippedContributions: ContributionDict;
  removedEmployees: ContributionDict;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  limit: number;
  offset: number;
  initialFetching: boolean;
  // written at init but never read; preserved from original state shape
  enableAutoPayroll: boolean;
  page: number;
  showAllLineItems: boolean;
  payPeriod?: PayPeriod;
}

export class Summary extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      limit: 25,
      offset: 0,
      initialFetching: true,
      enableAutoPayroll: false,
      page: 0,
      showAllLineItems: true,
      payPeriod: props.payroll,
    };
  }

  componentDidMount() {
    void this._getPaginatedContributions().then(() => {
      this.setState({ initialFetching: false });
    });
  }

  // StepWizard keeps inactive steps mounted, so the line-item list is frozen
  // after first visit. Re-fetch on re-entry so skipped/removed changes from
  // earlier steps are reflected here.
  componentDidUpdate(prevProps: Props) {
    if (!prevProps.isActive && this.props.isActive) {
      this.setState({ initialFetching: true });
      void this._getPaginatedContributions().then(() => {
        this.setState({ initialFetching: false });
      });
    }
  }

  _toggleShowAllLineItems = () => {
    const showAllLineItems = this.state.showAllLineItems;

    this.setState(
      { showAllLineItems: !showAllLineItems, offset: 0, page: 0 },
      () => {
        void this._getPaginatedContributions();
      }
    );
  };

  _getPaginatedContributions = () => {
    return this.props.getPaginatedContributions(
      this.props.client as ApolloClient<object>,
      {
        groupId: this.props.group.id,
        limit: this.state.limit,
        offset: this.state.offset,
        excludedIds: this.state.showAllLineItems
          ? []
          : this.props.usersToExclude,
        // injected by the parent on every render; non-null asserted to match
        // the service's required payPeriodId without changing runtime behavior
        payPeriodId: this.props.payPeriodId!,
      }
    );
  };

  _getStatus = ({
    employeeId,
    isUserWhoLeft,
    isLockedUser,
  }: ContributionEntry) => {
    if (this.props.keptPriorContributions[employeeId]) {
      return <StatusBox status="Using Prior Amount" type="dark" />;
    } else if (this.props.skippedContributions[employeeId]) {
      return <StatusBox status="Skipped" type="dark" />;
    } else if (this.props.approvedContributions[employeeId]) {
      return <StatusBox status="Approved" type="success" />;
    } else if (isLockedUser) {
      return <StatusBox status="Paused" type="warning" />;
    } else if (isUserWhoLeft || this.props.removedEmployees[employeeId]) {
      return <StatusBox status="Removed" type="danger" />;
    } else {
      return <StatusBox status="Approved Prior" type="success" />;
    }
  };

  _getContribsWithNoChange = () => {
    const {
      skippedContributions,
      removedEmployees,
      contributionsWithNoChange,
    } = this.props;
    // a previously approved contribution can still be changed by being skipped or employee removed
    const contributionsWithTrulyNoChange = chain(contributionsWithNoChange)
      .filter((contrib) => {
        return (
          !skippedContributions[contrib.employeeId] &&
          !removedEmployees[contrib.employeeId]
        );
      })
      .map((contrib) => {
        return {
          employeeId: contrib.employeeId,
          amount: contrib.amount,
        };
      })
      .value();

    return contributionsWithTrulyNoChange;
  };

  _buildPayrollInput() {
    // PayrollWorkflow's setters enforce mutual exclusion across the four
    // selection dicts, so we can build the payload without re-filtering here.
    // skippedContributions intentionally excludes newKeptPrior — a kept-prior
    // user contributes via a single lineItem; placing them in skippedItems
    // too would duplicate the same employee across lineItems and skippedItems.

    // all approved and existing contributions with no change
    const contribsWithNoChange = this._getContribsWithNoChange();
    const newApprovedContribs = map(
      this.props.approvedContributions,
      (contrib) => {
        return {
          employeeId: contrib.employeeId,
          amount: contrib.userNewContributionAmount,
        };
      }
    );
    const allApproved = [...contribsWithNoChange, ...newApprovedContribs];

    // all kept prior contributions
    const newKeptPrior = map(this.props.keptPriorContributions, (contrib) => {
      return {
        employeeId: contrib.employeeId,
        amount: contrib.userCurrentContributionAmount,
        skippedAmount: contrib.userNewContributionAmount,
      };
    });

    const newSkippedContributions = map(
      this.props.skippedContributions,
      // PRESERVE: latent bug — a skipped entry without `contribution` defaults
      // to {} so skippedAmount becomes undefined. Kept verbatim (locked by a
      // characterization test).
      ({ contribution = {}, employeeId }) => {
        return {
          employeeId,
          amount: 0,
          skippedAmount: (contribution as { amount?: number }).amount,
        };
      }
    );

    // all remove from plan, by link id
    const linkIds = map(this.props.removedEmployees, (removedUser) => {
      return (
        find(this.props.linkedEmployees, {
          employeeId: removedUser.employeeId,
        }) || {}
      ).id;
    });

    // The assembled values mirror the loose runtime shape (optional amounts /
    // ids surface as undefined). Cast once here to the strict GraphQL input
    // type the consumers expect, without altering the runtime payload.
    return {
      contributionsToProcess: [...allApproved, ...newKeptPrior],
      approvedContributions: map(this.props.approvedContributions, "id"),
      skippedContributions: [...newSkippedContributions],
      usersRemovedFromPlan: linkIds,
      totalAmount: this._getApprovedAmount(),
      groupId: this.props.group.id,
      isNextPayPeriod: this._isNextPayPeriod(),
      payPeriodId: this.props.payPeriodId!,
    } as unknown as ScheduledPayrollInput & {
      approvedContributions: string[];
      usersRemovedFromPlan: string[];
    };
  }

  _submitPayroll = () => {
    const promises = [];
    const payrollInput = this._buildPayrollInput();

    if (!isEmpty(this.props.approvedContributions)) {
      promises.push(
        this.props.approveContributionChanges(
          this.props.client as ApolloClient<object>,
          payrollInput.approvedContributions,
          this.props.group.id
        )
      );
    }

    if (!isEmpty(this.props.removedEmployees)) {
      promises.push(
        this.props.deleteEmployerLinks(
          this.props.client as ApolloClient<object>,
          payrollInput.usersRemovedFromPlan
        )
      );
    }

    void Promise.all(promises).then((results) => {
      if (results.some((result) => !result.success)) return;
      void this.props
        .approvePayroll(this.props.client as ApolloClient<object>, payrollInput)
        .then((result) => {
          if (result?.success) {
            toast.success("Successfully processed payroll.");
            this.props.push("/dashboard/contributions");
          }
        });
    });
  };

  _getSummaryData = (changes: ContributionEntry[]) => {
    return map(changes, (change) => {
      const {
        // preserve original throw-on-undefined: original destructured
        // change.userProfile with no default, so a missing profile throws
        userProfile: { firstName, lastName, lastFourOfSsn },
      } = change as Required<Pick<ContributionEntry, "userProfile">> &
        ContributionEntry;
      let amount;

      // if skipped or removed employee new amount is 0 prev amount should be last current;
      if (
        this.props.removedEmployees[change.employeeId] ||
        this.props.skippedContributions[change.employeeId]
      ) {
        amount = 0;

        // if using prior contribution then amount and prev amount become the same
      } else if (this.props.keptPriorContributions[change.employeeId]) {
        amount = change.userCurrentContributionAmount;
      } else if (has(change, "amount")) {
        amount = change.amount;
      } else {
        amount = change.userNewContributionAmount;
      }

      const fullName = `${firstName} ${lastName}`;

      const status = this._getStatus(change);

      const contributionAmount = formatCurrency(amount!);

      return {
        employeeName: fullName,
        lastFourOfSsn,
        contributionAmount,
        status,
        change,
      };
    });
  };

  _buildSummaryColumns = () => {
    const columns = [
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        label: "Contribution for Period	",
        key: "contributionAmount",
      },

      {
        label: "Status",
        key: "status",
      },
    ];
    return columns;
  };

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () =>
      this._getPaginatedContributions()
    );
  };

  _isNextPayPeriod = () => {
    const currentStatus = this.state.payPeriod!.status;
    return processedStatuses.includes(currentStatus!);
  };

  _getApprovedAmount = () => {
    const {
      skippedContributions,
      removedEmployees,
      keptPriorContributions,
      approvedContributions,
    } = this.props;
    const approvedAmount = reduce(
      this.props.allContributions,
      (acc, contrib) => {
        if (
          skippedContributions[contrib.employeeId] ||
          removedEmployees[contrib.employeeId]
        ) {
          return acc;
        }

        if (keptPriorContributions[contrib.employeeId]) {
          acc += contrib.userCurrentContributionAmount!;
          return acc;
        }

        // if amount property exist it means its a prior approved contribution
        if (has(contrib, "amount")) {
          acc += contrib.amount!;
          return acc;
        }

        if (
          approvedContributions[contrib.employeeId] &&
          contrib.userNewContributionAmount
        ) {
          acc += contrib.userNewContributionAmount;
          return acc;
        }

        return acc;
      },
      0
    );

    return formatAmount(approvedAmount);
  };

  _getSummarySection = () => {
    const processingDate = this.state.payPeriod!.onOrAfterPayrollProcessingDate
      ? moment().format("YYYY-MM-DD")
      : this.state.payPeriod!.payrollProcessingDate;

    const newlyApprovedAmt = this._getApprovedAmount();
    return (
      <div className="summary-info-wrapper">
        <p className="summary-title">Just a reminder</p>
        <AuthorizationText
          bankAccountNumber={this.props.bankAccountNumber}
          amount={newlyApprovedAmt}
          processingDate={processingDate}
        />
      </div>
    );
  };

  _getKpiSection = () => {
    const newlyApprovedAmt = this._getApprovedAmount();
    const numOfApproved = size(this.props.approvedContributions);
    const numOfSkipped = size(this.props.skippedContributions);
    const taxYear = this.props.payroll!.taxYear;

    return (
      <div className="kpis">
        <Kpi
          label="Total Contribution Amount"
          value={newlyApprovedAmt}
          format="currency"
        />
        <Kpi label="Approved Contributions" value={numOfApproved} />
        <Kpi label="Skipped Contributions" value={numOfSkipped} />
        <Kpi label="Tax Year" value={taxYear} />
      </div>
    );
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const changes = [
      ...(this.props.usersWhoLeftPlan || []),
      ...(this.props.lockedUserContributions || []),
      ...(this.props.contributionChanges || []),
    ];

    const noChanges = !this.state.showAllLineItems && isEmpty(changes);

    const showBtnLabel = this.state.showAllLineItems
      ? "Show Changes only"
      : "Show All";

    const payrollPeriod = this.state.payPeriod;

    const schedule = payrollPeriod!;

    const columns = this._buildSummaryColumns();
    const data = this._getSummaryData(changes);

    return (
      <div>
        {this._getSummarySection()}
        {this._getKpiSection()}
        <Card>
          <div className="workflow-widget">
            <div className="action-header">
              <span className="group-info">
                <span className="group-name">
                  {this.props.group.name}:{" "}
                  <span className="invite-number">
                    Total contributions{" "}
                    {this.state.showAllLineItems ? "" : "with changes"}
                    <span className="number">({changes.length})</span>
                  </span>
                </span>
              </span>

              <div className="action-btns">
                <PayrollStatementDownloader
                  endDate={schedule.payPeriodEnd!}
                  startDate={schedule.payPeriodStart!}
                  bankAccountNumber={this.props.bankAccountNumber}
                  group={this.props.group}
                  payrollInput={
                    this._buildPayrollInput() as unknown as {
                      contributionsToProcess: Array<{
                        employeeId: string;
                        amount: number;
                        skippedAmount?: number;
                      }>;
                      skippedContributions: Array<{
                        employeeId: string;
                        amount: number;
                        skippedAmount?: number;
                      }>;
                    }
                  }
                  payrollType={"SCHEDULED"}
                  totalAmount={this._getApprovedAmount()}
                  text={"Download Contributions"}
                />
                <span style={{ paddingLeft: "15px" }}>
                  <Button
                    color="secondary"
                    name="action"
                    type="button"
                    onClick={this._toggleShowAllLineItems}
                    btnLabel={showBtnLabel}
                    size="sm"
                  />
                </span>
              </div>
            </div>
          </div>

          <span>
            {this.props.isFetching && <IconSpinner centered />}
            {!noChanges && (
              <>
                <IconTable columns={columns} data={data} />

                <div className="pagination-row">
                  <Paginator
                    onChange={this._onPageChange}
                    pageCount={
                      this.props.contributionChangesCount / this.state.limit
                    }
                    page={this.state.page}
                  />
                </div>
              </>
            )}
            {noChanges && (
              <p className="blank-slate-msg">
                There are no contribution changes for this pay period.
              </p>
            )}
          </span>
        </Card>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="process-payroll-button-row">
          <Button
            icon={{
              icon: <FiChevronLeft size={14} stroke={"#0cb7c4"} />,
              position: "left",
            }}
            onClick={() => this.props.goToNamedStep!("approvals")}
            btnLabel="Go Back"
            name="cancel"
            color="cancel"
          />
          <Button
            name="submit"
            btnLabel="Continue"
            onClick={this._submitPayroll}
            withArrow
            loading={this.props.isUpdating}
          />
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Summary));
